import * as React from 'react';

import classnames from 'classnames';
import _ from 'underscore';

// @ts-expect-error
import {ScrollButton} from 'shells/button/scroll-button/scroll-button';

import type {Lead as LeadFragment} from 'nutshell-graphql-types';

import {LeadPipelineHeader} from './lead-pipeline-header';
import {StagesCarouselDeletedStagePseudoChevron} from './stages-carousel-deleted-stage-pseudo-chevron';
import {StagesCarouselStages} from './stages-carousel-stages';

import styles from './stages-carousel.module.css';

type Props = {
    lead: LeadFragment;
    temporarilySelectedStageId: string | null | undefined;
    onTemporarilySelectStage: (id: string) => any;
    onClearTemporaryStageSelection: () => any;
    onRefetchLead: () => any;
    isWidget: boolean;
};

export class StagesCarousel extends React.PureComponent<
    Props,
    {
        canScrollLeft: boolean;
        canScrollRight: boolean;
        isMouseHoveringScrollLeft: boolean;
        isMouseHoveringScrollRight: boolean;
    }
> {
    stagesScrollRef: HTMLElement | null | undefined;
    stagesContainerRef: HTMLElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();

        this.state = {
            canScrollLeft: false,
            canScrollRight: false,
            isMouseHoveringScrollLeft: false,
            isMouseHoveringScrollRight: false,
        };
    }

    componentDidMount() {
        this.updateScrollButtonsUI();
        $(window).on('resize', this.handleWindowResize);
        if (this.props.lead.stages.current) {
            setTimeout(this.scrollToStage, 500, this.props.lead.stages.current.id);
        }
    }

    componentWillUnmount() {
        $(window).off('resize', this.handleWindowResize);
    }

    render() {
        const {canScrollLeft, canScrollRight} = this.state;

        const classes = classnames('ui-stages-carousel', {
            'state-hover-scroll-left': this.state.isMouseHoveringScrollLeft,
            'state-hover-scroll-right': this.state.isMouseHoveringScrollRight,
            widget: this.props.isWidget,
        });

        return (
            <div className={classes}>
                {!this.props.isWidget && <LeadPipelineHeader lead={this.props.lead} />}
                {this.closedLeadInDeletedStage() ? (
                    <StagesCarouselDeletedStagePseudoChevron
                        deletedMilestoneName={this.getDeletedMilestoneName()}
                        leadOutcomeColorVariant={this.getLeadOutcomeColorVariant()}
                    />
                ) : (
                    <div className='inner-wrapper'>
                        <div
                            ref={(node) => {
                                this.stagesScrollRef = node;
                            }}
                            className='ui-stages-carousel__scroll-container'
                            onScroll={this.handleScroll}
                        >
                            <StagesCarouselStages
                                getStagesRef={(ref) => {
                                    this.stagesContainerRef = ref;
                                }}
                                lead={this.props.lead}
                                onTemporarilySelectStage={this.handleTemporarilyStageSelect}
                                onClearTemporaryStageSelection={
                                    this.props.onClearTemporaryStageSelection
                                }
                                temporarilySelectedStageId={this.props.temporarilySelectedStageId}
                                onRefetchLead={this.props.onRefetchLead}
                            />
                        </div>
                        <ScrollButton
                            onClick={this.handleClickScrollLeft}
                            onMouseEnter={this.setScrollLeftHoverState}
                            onMouseLeave={this.clearScrollLeftHoverState}
                            orientation='left'
                            iconClassName={styles['chevron-left']}
                            className={
                                canScrollLeft
                                    ? styles['scroll-button-left-active']
                                    : styles['scroll-button-left']
                            }
                        />
                        <ScrollButton
                            onClick={this.handleClickScrollRight}
                            onMouseEnter={this.setScrollRightHoverState}
                            onMouseLeave={this.clearScrollRightHoverState}
                            orientation='right'
                            iconClassName={styles['chevron-right']}
                            className={
                                canScrollRight
                                    ? styles['scroll-button-right-active']
                                    : styles['scroll-button-right']
                            }
                        />
                    </div>
                )}
            </div>
        );
    }

    handleTemporarilyStageSelect = (id: string) => {
        this.scrollToStage(id);
        this.props.onTemporarilySelectStage(id);
    };

    setScrollLeftHoverState = () => {
        this.setState({isMouseHoveringScrollLeft: true});
    };

    clearScrollLeftHoverState = () => {
        this.setState({isMouseHoveringScrollLeft: false});
    };

    setScrollRightHoverState = () => {
        this.setState({isMouseHoveringScrollRight: true});
    };

    clearScrollRightHoverState = () => {
        this.setState({isMouseHoveringScrollRight: false});
    };

    handleClickScrollLeft = () => {
        const stages = this.stagesScrollRef;
        if (!stages) return;

        this.clearScrollLeftHoverState();

        // Scroll half the width of the container so you dont lose your place
        const {offsetWidth, scrollLeft} = stages;
        const scrollToPosition = scrollLeft - offsetWidth / 2;

        $(stages).animate({scrollLeft: scrollToPosition}, 800, 'easeOutExpo');
    };

    handleClickScrollRight = () => {
        const stages = this.stagesScrollRef;
        if (!stages) return;

        this.clearScrollRightHoverState();

        // Scroll half the width of the container so you dont lose your place
        const {offsetWidth, scrollLeft} = stages;
        const scrollToPosition = scrollLeft + offsetWidth / 2;

        $(stages).animate({scrollLeft: scrollToPosition}, 800, 'easeOutExpo');
    };

    handleScroll = _.debounce(() => this.updateScrollButtonsUI(), 100);

    updateScrollButtonsUI = () => {
        if (!this.stagesScrollRef) return;

        const {offsetWidth, scrollLeft, scrollWidth} = this.stagesScrollRef;
        const threshold = 50;
        const canScrollRight = scrollLeft + offsetWidth <= scrollWidth - threshold;
        const canScrollLeft = scrollLeft >= threshold;

        this.setState({
            canScrollLeft: canScrollLeft,
            canScrollRight: canScrollRight,
        });
    };

    handleWindowResize = _.debounce(() => this.updateScrollButtonsUI(), 500);

    scrollToStage = (stageId: string) => {
        const stages = this.stagesContainerRef;
        const scrollContainer = this.stagesScrollRef;
        if (!stages || !scrollContainer) return;

        const index = _.findIndex(
            this.props.lead.stages.edges,
            (stageEdge) => stageEdge.node.id === stageId
        );
        const activeStageNode = stages.children[index];

        if (this.props.lead.stages.totalCount === 0 || _.isUndefined(activeStageNode)) return;

        // @ts-expect-error
        const offsetLeft = activeStageNode.offsetLeft + activeStageNode.offsetWidth / 2;
        const viewPortWidth = $(scrollContainer).width();

        // @ts-expect-error
        const scrollToPosition = offsetLeft - viewPortWidth / 2;
        const scrollLeft = scrollToPosition >= 0 ? scrollToPosition : 0;

        $(scrollContainer).animate(
            {scrollLeft: scrollLeft},
            800,
            'easeInOutSine',
            this.handleScroll
        );
    };

    closedLeadInDeletedStage = () => {
        return Boolean(
            (this.props.lead.outcome || this.props.lead.deletedTime) &&
                this.props.lead.stages.current &&
                this.props.lead.stages.current.deletedTime
        );
    };

    getLeadOutcomeColorVariant = () => {
        const status = this.props.lead.outcome ? this.props.lead.outcome.name : null;

        if (status === 'Won') {
            return 'green';
        }

        if (this.props.lead.isHot) {
            return 'orange';
        }

        return 'grey';
    };

    getDeletedMilestoneName = () => {
        if (this.props.lead.stages.current) {
            return this.props.lead.stages.current.name;
        }

        return 'Unknown Stage';
    };
}
